import React, { useEffect, useState, Component } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { MapPinIcon, NavigationIcon, CalendarIcon, ClockIcon, SearchIcon, PhoneIcon, MailIcon, ExternalLinkIcon, InfoIcon, BuildingIcon, AlertTriangleIcon } from 'lucide-react';
// Mock data for locations
const mockLocations = [{
  id: 1,
  name: 'Slipbaan Rotterdam',
  address: 'Slipbaanweg 10, 3045 Rotterdam',
  coordinates: {
    lat: 51.9225,
    lng: 4.4792
  },
  phone: '010-1234567',
  email: 'rotterdam@vvgn.nl',
  website: 'https://www.vvgn.nl/locaties/rotterdam',
  facilities: ['Slipbaan', 'Leslokaal', 'Kantine', 'Parkeerplaats'],
  openingHours: '08:00 - 18:00',
  notes: 'Meld je bij de receptie bij aankomst.',
  upcomingTrainings: [{
    id: 1,
    date: new Date(),
    startTime: new Date(new Date().setHours(9, 0)),
    endTime: new Date(new Date().setHours(12, 0)),
    type: 'Sliptraining Basis',
    client: 'Gemeente Rotterdam'
  }, {
    id: 2,
    date: new Date(new Date().setDate(new Date().getDate() + 2)),
    startTime: new Date(new Date().setDate(new Date().getDate() + 2)).setHours(13, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() + 2)).setHours(16, 0),
    type: 'Sliptraining Gevorderd',
    client: 'Shell Nederland'
  }],
  imageUrl: 'https://images.unsplash.com/photo-1617886322168-72b886573c5f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80'
}, {
  id: 2,
  name: 'Trainingscentrum Utrecht',
  address: 'Defensieweg 5, 3584 Utrecht',
  coordinates: {
    lat: 52.0842,
    lng: 5.1666
  },
  phone: '030-9876543',
  email: 'utrecht@vvgn.nl',
  website: 'https://www.vvgn.nl/locaties/utrecht',
  facilities: ['Oefenterrein', 'Leslokaal', 'Kantine', 'Parkeerplaats'],
  openingHours: '08:30 - 17:30',
  notes: 'Parkeren kan op het terrein. Volg de borden "Bezoekers".',
  upcomingTrainings: [{
    id: 3,
    date: new Date(),
    startTime: new Date(new Date().setHours(13, 30)),
    endTime: new Date(new Date().setHours(16, 30)),
    type: 'Defensief Rijden',
    client: 'ProRail'
  }],
  imageUrl: 'https://images.unsplash.com/photo-1572025442646-866d16c84a54?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80'
}, {
  id: 3,
  name: 'Trainingslocatie Amsterdam',
  address: 'Transportweg 8, 1043 Amsterdam',
  coordinates: {
    lat: 52.3667,
    lng: 4.9
  },
  phone: '020-5678901',
  email: 'amsterdam@vvgn.nl',
  website: 'https://www.vvgn.nl/locaties/amsterdam',
  facilities: ['Oefenterrein', 'Leslokaal', 'Parkeerplaats'],
  openingHours: '08:00 - 18:00',
  notes: 'Let op: beperkte parkeergelegenheid. Kom indien mogelijk met OV.',
  upcomingTrainings: [{
    id: 4,
    date: new Date(new Date().setDate(new Date().getDate() + 1)),
    startTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(9, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(13, 0),
    type: 'Code 95 Training',
    client: 'Transport BV'
  }],
  imageUrl: 'https://images.unsplash.com/photo-1543674892-7d64d45facbe?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80'
}];
// Helper function to format time
const formatTime = date => {
  return date.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit'
  });
};
// Helper function to format date
const formatDate = date => {
  return date.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
};
export function LocationRoute() {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  // Filter locations based on search term
  const filteredLocations = mockLocations.filter(location => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return location.name.toLowerCase().includes(searchLower) || location.address.toLowerCase().includes(searchLower);
    }
    return true;
  });
  // Handle location selection
  const handleSelectLocation = location => {
    setSelectedLocation(location);
  };
  // Handle opening navigation
  const handleOpenNavigation = address => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };
  // Initialize map when a location is selected
  useEffect(() => {
    if (selectedLocation && !mapLoaded) {
      // In a real app, this would initialize a map with the location's coordinates
      // For this demo, we'll just simulate map loading
      setTimeout(() => {
        setMapLoaded(true);
      }, 500);
    }
  }, [selectedLocation]);
  return <div className="space-y-6" data-id="element-1191">
      <div className="flex justify-between items-center" data-id="element-1192">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1193">Locatie & Route</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1194">
        {/* Locations list */}
        <div className="lg:col-span-1" data-id="element-1195">
          <Card data-id="element-1196">
            <div className="p-4 border-b border-gray-200" data-id="element-1197">
              <div className="relative" data-id="element-1198">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-1199" />
                <input type="text" placeholder="Zoek locaties..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1200" />
              </div>
            </div>
            <div className="p-4 max-h-[600px] overflow-y-auto" data-id="element-1201">
              {filteredLocations.length > 0 ? <div className="space-y-3" data-id="element-1202">
                  {filteredLocations.map(location => <div key={location.id} className={`p-3 rounded-md border cursor-pointer transition-colors ${selectedLocation?.id === location.id ? 'bg-gray-100 border-[#B61C1C]' : 'bg-white border-gray-200 hover:bg-gray-50'}`} onClick={() => handleSelectLocation(location)} data-id="element-1203">
                      <div className="flex justify-between items-start" data-id="element-1204">
                        <div data-id="element-1205">
                          <h3 className="font-medium text-gray-900" data-id="element-1206">
                            {location.name}
                          </h3>
                          <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-1207">
                            <MapPinIcon size={12} className="mr-1" data-id="element-1208" />
                            <span data-id="element-1209">{location.address}</span>
                          </div>
                          <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-1210">
                            <ClockIcon size={12} className="mr-1" data-id="element-1211" />
                            <span data-id="element-1212">{location.openingHours}</span>
                          </div>
                        </div>
                        <div data-id="element-1213">
                          <Badge variant="info" data-id="element-1214">
                            {location.upcomingTrainings.length} trainingen
                          </Badge>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-6" data-id="element-1215">
                  <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-1216" />
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-1217">
                    Geen locaties gevonden
                  </h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1218">
                    Pas je zoekopdracht aan
                  </p>
                </div>}
            </div>
          </Card>
        </div>
        {/* Location details */}
        <div className="lg:col-span-2" data-id="element-1219">
          {selectedLocation ? <div className="space-y-4" data-id="element-1220">
              <Card data-id="element-1221">
                <div className="relative h-48 bg-gray-200 overflow-hidden" data-id="element-1222">
                  <img src={selectedLocation.imageUrl} alt={selectedLocation.name} className="w-full h-full object-cover" data-id="element-1223" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4" data-id="element-1224">
                    <h2 className="text-xl font-bold text-white" data-id="element-1225">
                      {selectedLocation.name}
                    </h2>
                    <p className="text-sm text-white/80" data-id="element-1226">
                      {selectedLocation.address}
                    </p>
                  </div>
                </div>
                <div className="p-4" data-id="element-1227">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1228">
                    <div data-id="element-1229">
                      <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1230">
                        Contact Informatie
                      </h3>
                      <div className="space-y-2" data-id="element-1231">
                        <div className="flex items-center text-sm" data-id="element-1232">
                          <PhoneIcon size={16} className="mr-2 text-gray-500" data-id="element-1233" />
                          <a href={`tel:${selectedLocation.phone}`} className="text-[#B61C1C] hover:underline" data-id="element-1234">
                            {selectedLocation.phone}
                          </a>
                        </div>
                        <div className="flex items-center text-sm" data-id="element-1235">
                          <MailIcon size={16} className="mr-2 text-gray-500" data-id="element-1236" />
                          <a href={`mailto:${selectedLocation.email}`} className="text-[#B61C1C] hover:underline" data-id="element-1237">
                            {selectedLocation.email}
                          </a>
                        </div>
                        <div className="flex items-center text-sm" data-id="element-1238">
                          <ExternalLinkIcon size={16} className="mr-2 text-gray-500" data-id="element-1239" />
                          <a href={selectedLocation.website} target="_blank" rel="noopener noreferrer" className="text-[#B61C1C] hover:underline" data-id="element-1240">
                            Website
                          </a>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1241">
                      <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1242">
                        Faciliteiten
                      </h3>
                      <div className="flex flex-wrap gap-2" data-id="element-1243">
                        {selectedLocation.facilities.map((facility, index) => <Badge key={index} variant="default" data-id="element-1244">
                            {facility}
                          </Badge>)}
                      </div>
                    </div>
                  </div>
                  {selectedLocation.notes && <div className="mt-4 p-3 bg-yellow-50 border border-yellow-100 rounded-md" data-id="element-1245">
                      <div className="flex" data-id="element-1246">
                        <InfoIcon size={16} className="mr-2 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1247" />
                        <p className="text-sm text-yellow-800" data-id="element-1248">
                          {selectedLocation.notes}
                        </p>
                      </div>
                    </div>}
                  <div className="mt-4" data-id="element-1249">
                    <Button variant="primary" onClick={() => handleOpenNavigation(selectedLocation.address)} data-id="element-1250">
                      <NavigationIcon size={16} className="mr-2" data-id="element-1251" />
                      Open Routeplanner
                    </Button>
                  </div>
                </div>
              </Card>
              {/* Map */}
              <Card data-id="element-1252">
                <div className="p-4 border-b border-gray-200" data-id="element-1253">
                  <h3 className="font-medium text-gray-900" data-id="element-1254">Kaart</h3>
                </div>
                <div className="p-4" data-id="element-1255">
                  <div className="bg-gray-100 h-64 rounded-md flex items-center justify-center" data-id="element-1256">
                    {mapLoaded ? <div className="text-center" data-id="element-1257">
                        <p className="text-sm text-gray-500" data-id="element-1258">
                          Hier zou normaal een interactieve kaart getoond worden
                          met de locatie:
                        </p>
                        <p className="font-medium mt-2" data-id="element-1259">
                          {selectedLocation.name} - {selectedLocation.address}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-1260">
                          Coördinaten: {selectedLocation.coordinates.lat},{' '}
                          {selectedLocation.coordinates.lng}
                        </p>
                        <Button variant="outline" size="sm" className="mt-4" onClick={() => handleOpenNavigation(selectedLocation.address)} data-id="element-1261">
                          <NavigationIcon size={16} className="mr-2" data-id="element-1262" />
                          Open in Google Maps
                        </Button>
                      </div> : <div className="text-center" data-id="element-1263">
                        <p className="text-sm text-gray-500" data-id="element-1264">
                          Kaart wordt geladen...
                        </p>
                      </div>}
                  </div>
                </div>
              </Card>
              {/* Upcoming trainings */}
              <Card data-id="element-1265">
                <div className="p-4 border-b border-gray-200" data-id="element-1266">
                  <h3 className="font-medium text-gray-900" data-id="element-1267">
                    Geplande Trainingen op deze Locatie
                  </h3>
                </div>
                <div className="p-4" data-id="element-1268">
                  {selectedLocation.upcomingTrainings.length > 0 ? <div className="space-y-3" data-id="element-1269">
                      {selectedLocation.upcomingTrainings.map(training => <div key={training.id} className="p-3 bg-gray-50 rounded-md" data-id="element-1270">
                          <div className="flex justify-between items-start" data-id="element-1271">
                            <div data-id="element-1272">
                              <h4 className="font-medium text-gray-900" data-id="element-1273">
                                {training.type}
                              </h4>
                              <p className="text-sm text-gray-500" data-id="element-1274">
                                {training.client}
                              </p>
                              <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-1275">
                                <CalendarIcon size={12} className="mr-1" data-id="element-1276" />
                                <span data-id="element-1277">
                                  {formatDate(new Date(training.date))}
                                </span>
                              </div>
                              <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-1278">
                                <ClockIcon size={12} className="mr-1" data-id="element-1279" />
                                <span data-id="element-1280">
                                  {formatTime(new Date(training.startTime))} -{' '}
                                  {formatTime(new Date(training.endTime))}
                                </span>
                              </div>
                            </div>
                            <Badge variant={new Date(training.date).toDateString() === new Date().toDateString() ? 'info' : 'default'} data-id="element-1281">
                              {new Date(training.date).toDateString() === new Date().toDateString() ? 'Vandaag' : 'Gepland'}
                            </Badge>
                          </div>
                        </div>)}
                    </div> : <div className="text-center py-6" data-id="element-1282">
                      <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-1283" />
                      <h3 className="text-sm font-medium text-gray-900" data-id="element-1284">
                        Geen trainingen gepland
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1285">
                        Er zijn momenteel geen trainingen gepland op deze
                        locatie
                      </p>
                    </div>}
                </div>
              </Card>
            </div> : <Card data-id="element-1286">
              <div className="p-6 text-center" data-id="element-1287">
                <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-1288" />
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1289">
                  Geen locatie geselecteerd
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1290">
                  Selecteer een locatie om details en route-informatie te
                  bekijken
                </p>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}